import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGameContext } from '../contexts/GameContext';
import { useTheme } from '../contexts/ThemeContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { GameScenarioContent, ScenarioIncident } from '../types/ScenarioManagement';
import { DecisionTreeVisualization } from '../components/DecisionTreeVisualization';
import { ScenarioRiskCalculator } from '../components/ScenarioRiskCalculator';
import { EmailGateModal } from '../components/EmailGateModal';
import { Footer } from '../components/Footer';
import { getIncidentCostBreakdown } from '../data/incidentCosts';
import {
  buildDecisionTree,
  buildTreeStructure,
  getPathComparison
} from '../services/DecisionTreeService';
import { Incident } from '../types/Incident';
import { trackEmailGateEvent } from '../services/EmailGatingService';

const FinalScoringScreen: React.FC = () => {
  const navigate = useNavigate();
  const { gameState, calculateFinalScore, resetGame, setGamePhase } = useGameContext();
  const { isLight } = useTheme();
  const { preferences, setEmail, hasEmail } = useUserPreferences();
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const scoreBreakdown = gameState.scoreBreakdown;

  // Get scoring weights for prevention scoring
  const scenarioContent = gameState.selectedScenario?.content as GameScenarioContent | undefined;
  const scoringWeights = scenarioContent?.gameSettings?.scoringWeights;

  // Convert ScenarioIncidents to Incidents
  const incidents: Incident[] = useMemo(() => {
    return gameState.availableIncidents.map((inc: ScenarioIncident) => ({
      id: inc.id,
      title: inc.title,
      description: inc.description,
      severity: inc.severity,
      category: inc.category,
      timeLimit: inc.timeLimit,
      potentialImpact: inc.potentialImpact,
      responseOptions: inc.responseOptions
    }));
  }, [gameState.availableIncidents]);

  // Build decision tree data
  const treeData = useMemo(() => {
    if (incidents.length > 0 && gameState.incidentResponses.length > 0) {
      return buildDecisionTree(incidents, gameState.incidentResponses, scoringWeights);
    }
    return null;
  }, [incidents, gameState.incidentResponses, scoringWeights]);

  // Build tree structure for visualization
  const treeStructure = useMemo(() => {
    if (incidents.length > 0 && gameState.incidentResponses.length > 0) {
      return buildTreeStructure(incidents, gameState.incidentResponses, scoringWeights);
    }
    return null;
  }, [incidents, gameState.incidentResponses, scoringWeights]);

  // Calculate scores when component mounts if not already calculated
  useEffect(() => {
    if (!scoreBreakdown && gameState.incidentResponses.length > 0) {
      calculateFinalScore();
    }
  }, [scoreBreakdown, gameState.incidentResponses.length, calculateFinalScore]);

  // Track exercise completion
  useEffect(() => {
    if (treeData && treeData.playerPath.nodes.length > 0) {
      trackEmailGateEvent('exercise_complete', {
        incidentsHandled: treeData.playerPath.nodes.length,
        scenarioId: gameState.selectedScenario?.id,
      });
    }
  }, [treeData, gameState.selectedScenario?.id]);

  const handleStartNewSimulation = () => {
    trackEmailGateEvent('simulation_restart', {
      previousScenarioId: gameState.selectedScenario?.id,
    });
    resetGame();
    setGamePhase('scenario-selection');
    navigate('/');
  };

  const handleEmailSubmit = (email: string) => {
    setEmail(email);
    setShowEmailCapture(false);
    trackEmailGateEvent('email_gate_submit_post_exercise', {
      role: preferences.role,
      industry: preferences.industry,
    });
  };

  const handleEmailSkip = () => {
    setShowEmailCapture(false);
  };

  // Handle empty state
  if (!treeData || !treeStructure) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isLight
          ? 'bg-gradient-to-br from-slate-50 to-slate-100'
          : 'bg-gradient-to-br from-dark-primary to-dark-secondary'
      }`}>
        <div className="text-center">
          <p className={`text-xl mb-4 ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>
            No decision data available.
          </p>
          <button
            onClick={handleStartNewSimulation}
            className={`px-6 py-3 rounded-lg font-semibold ${
              isLight
                ? 'bg-professional-blue-600 text-white hover:bg-professional-blue-700'
                : 'bg-professional-blue-600 text-white hover:bg-professional-blue-700'
            }`}
          >
            Start New Simulation
          </button>
        </div>
      </div>
    );
  }

  const comparison = getPathComparison(treeData);

  return (
    <div
      className={`min-h-screen py-8 px-4 transition-colors duration-300 ${
        isLight
          ? 'bg-gradient-to-br from-slate-50 to-slate-100'
          : 'bg-gradient-to-br from-dark-primary to-dark-secondary'
      }`}
    >
      <motion.div
        className="max-w-7xl mx-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          <h1 className={`text-3xl sm:text-4xl font-bold ${
            isLight ? 'text-slate-900' : 'text-white'
          }`}>
            Final Results
          </h1>
        </motion.div>

        {/* Performance Summary Card */}
        <motion.div
          className={`rounded-2xl p-6 mb-8 ${
            isLight
              ? 'bg-white shadow-xl border border-slate-200'
              : 'bg-white/[0.03] border border-white/10 backdrop-blur-md'
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center">
              <div className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                Incidents Handled
              </div>
              <div className={`text-3xl font-bold ${isLight ? 'text-slate-800' : 'text-white'}`}>
                {treeData.playerPath.nodes.length}
              </div>
            </div>
            <div className="text-center">
              <div className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                Your Average Score
              </div>
              <div className={`text-3xl font-bold ${
                comparison.playerScore >= 61
                  ? isLight ? 'text-green-600' : 'text-green-400'
                  : comparison.playerScore >= 31
                  ? isLight ? 'text-yellow-600' : 'text-yellow-400'
                  : isLight ? 'text-red-600' : 'text-red-400'
              }`}>
                {comparison.playerScore}
              </div>
            </div>
            <div className="text-center">
              <div className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                Improvement Potential
              </div>
              <div className={`text-3xl font-bold ${
                comparison.improvementPotential > 0
                  ? isLight ? 'text-amber-600' : 'text-amber-400'
                  : isLight ? 'text-green-600' : 'text-green-400'
              }`}>
                {comparison.improvementPotential > 0 ? `+${comparison.improvementPotential}` : 'Max!'}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Decision Tree Section */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <DecisionTreeVisualization
            treeStructure={treeStructure}
            treeData={treeData}
          />
        </motion.div>

        {/* Insights Section */}
        <motion.div
          className={`rounded-2xl p-6 mb-8 ${
            isLight
              ? 'bg-white shadow-xl border border-slate-200'
              : 'bg-white/[0.03] border border-white/10 backdrop-blur-md'
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <h3 className={`text-lg font-semibold mb-4 ${isLight ? 'text-slate-800' : 'text-white'}`}>
            Key Insights
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`p-4 rounded-lg ${
              isLight ? 'bg-slate-50' : 'bg-white/5'
            }`}>
              <h4 className={`text-sm font-medium mb-2 ${
                isLight ? 'text-slate-700' : 'text-slate-300'
              }`}>
                Best Decision
              </h4>
              {(() => {
                const bestNode = treeData.playerPath.nodes.reduce(
                  (best, node) => node.overallScore > best.overallScore ? node : best,
                  treeData.playerPath.nodes[0]
                );
                return bestNode ? (
                  <p className={`text-sm ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
                    <span className="font-medium">{bestNode.incidentTitle}</span>: {bestNode.responseLabel} (Score: {bestNode.overallScore})
                  </p>
                ) : null;
              })()}
            </div>
            <div className={`p-4 rounded-lg ${
              isLight ? 'bg-slate-50' : 'bg-white/5'
            }`}>
              <h4 className={`text-sm font-medium mb-2 ${
                isLight ? 'text-slate-700' : 'text-slate-300'
              }`}>
                Area for Improvement
              </h4>
              {(() => {
                const worstNode = treeData.playerPath.nodes.reduce(
                  (worst, node) => node.overallScore < worst.overallScore ? node : worst,
                  treeData.playerPath.nodes[0]
                );
                return worstNode ? (
                  <p className={`text-sm ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
                    <span className="font-medium">{worstNode.incidentTitle}</span>: {worstNode.responseLabel} (Score: {worstNode.overallScore})
                  </p>
                ) : null;
              })()}
            </div>
          </div>
        </motion.div>

        {/* Scenario Risk Calculator Section */}
        {(() => {
          // Build incident scores for all incidents with cost data
          const incidentScores = treeData.playerPath.nodes
            .filter(node => getIncidentCostBreakdown(node.incidentId) !== null)
            .map(node => ({
              incidentId: node.incidentId,
              incidentTitle: node.incidentTitle,
              responseScore: node.overallScore,
              responseLabel: node.responseLabel,
            }));

          if (incidentScores.length > 0) {
            const scenarioTitle = gameState.selectedScenario?.title || 'Risk Simulation';
            return (
              <motion.div
                className="mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
              >
                <ScenarioRiskCalculator
                  scenarioTitle={scenarioTitle}
                  incidentScores={incidentScores}
                />
              </motion.div>
            );
          }
          return null;
        })()}

        {/* Facilitated Session CTA */}
        <motion.div
          className={`rounded-2xl p-6 md:p-8 mb-8 ${
            isLight
              ? 'bg-gradient-to-br from-professional-blue-50 to-indigo-50 border border-professional-blue-200 shadow-xl'
              : 'bg-gradient-to-br from-aurora-violet-900/20 to-aurora-cyan-900/20 border border-aurora-violet-500/30 backdrop-blur-md'
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Icon */}
            <div className={`flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center ${
              isLight ? 'bg-professional-blue-100' : 'bg-aurora-violet-500/20'
            }`}>
              <svg
                className={`w-8 h-8 ${isLight ? 'text-professional-blue-600' : 'text-aurora-violet-400'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>

            {/* Content */}
            <div className="flex-1 text-center md:text-left">
              <h3 className={`text-xl md:text-2xl font-semibold mb-2 ${
                isLight ? 'text-slate-900' : 'text-white'
              }`}>
                Want to run this with your team?
              </h3>
              <p className={`mb-0 md:mb-0 ${
                isLight ? 'text-slate-600' : 'text-slate-300'
              }`}>
                IOmergent facilitates in-person tabletop exercises for executive and technical teams.
                Build alignment and test your incident response capabilities together.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex-shrink-0 flex flex-col sm:flex-row gap-3">
              {/* Get the Guide - Email capture (only show if no email) */}
              {!hasEmail && (
                <motion.button
                  onClick={() => {
                    trackEmailGateEvent('email_cta_click_post_exercise');
                    setShowEmailCapture(true);
                  }}
                  className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                    isLight
                      ? 'bg-white text-professional-blue-600 border border-professional-blue-300 hover:bg-professional-blue-50 hover:shadow-md'
                      : 'bg-white/10 text-white border border-aurora-violet-400/50 hover:bg-white/20'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Get the Guide
                </motion.button>
              )}

              {/* Schedule a Conversation - Primary CTA */}
              <motion.a
                href="https://iomergent.com/connect/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEmailGateEvent('facilitated_cta_click')}
                className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  isLight
                    ? 'bg-professional-blue-600 text-white hover:bg-professional-blue-700 hover:shadow-lg'
                    : 'bg-aurora-violet-600 text-white hover:bg-aurora-violet-500 hover:shadow-aurora-violet'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Schedule a Conversation
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </motion.a>
            </div>
          </div>
        </motion.div>

        {/* Start New Simulation Button */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
        >
          <motion.button
            onClick={handleStartNewSimulation}
            className={`font-bold py-4 px-8 rounded-lg text-lg shadow-lg transition-all duration-200 uppercase tracking-wider ${
              isLight
                ? 'bg-gradient-to-r from-professional-blue-600 to-professional-blue-700 text-white hover:shadow-xl'
                : 'bg-gradient-to-r from-professional-blue-600 to-professional-blue-700 text-white hover:shadow-xl'
            }`}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            Start New Simulation
          </motion.button>
        </motion.div>

        {/* Footer */}
        <Footer />
      </motion.div>

      {/* Email Capture Modal */}
      <EmailGateModal
        isOpen={showEmailCapture}
        onSubmit={handleEmailSubmit}
        onSkip={handleEmailSkip}
        role={preferences.role}
        industry={preferences.industry}
        mode="post-exercise"
      />
    </div>
  );
};

export default FinalScoringScreen;
